import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LogOut, Plus, Calendar as CalendarIcon, Target, Wallet, Lightbulb, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import CalendarSelector from '../components/CalendarSelector';
import BudgetCalendar from '../components/BudgetCalendar';
import SavingsGoals from '../components/SavingsGoals';
import Portefeuilles from '../components/Portefeuilles';
import TransactionHistory from '../components/TransactionHistory';
import AstucesTab from '../components/AstucesTab';
import FinancialFoundation from '../components/FinancialFoundation';
import FinancialSetupWizard from '../components/FinancialSetupWizard';
import CreateCalendarDialog from '../components/CreateCalendarDialog';
import BirthdayCelebration from '../components/BirthdayCelebration';
import TropicalBackground from '../components/TropicalBackground';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, setUser, onLogout }) => {
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [showCreateCalendar, setShowCreateCalendar] = useState(false);
  const [showFinancialSetup, setShowFinancialSetup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userFirstName, setUserFirstName] = useState(null);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('calendar');
  const navigate = useNavigate();

  // Trigger calendar refresh when data changes in other modules
  const triggerCalendarRefresh = () => {
    setCalendarRefreshKey(prev => prev + 1);
  };

  useEffect(() => {
    loadCalendars();
    checkUserProfile();
  }, []);

  const checkUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`, { withCredentials: true });
      const profile = response?.data;
      
      // Safely check profile data
      if (!profile) {
        console.warn('Profile data is null or undefined');
        setIsFirstTimeUser(true);
        return;
      }
      
      if (!profile.has_completed_profile) {
        // First-time user - will show unified wizard
        setIsFirstTimeUser(true);
      } else {
        setUserFirstName(profile.first_name || null);
        setIsFirstTimeUser(false);
      }
    } catch (error) {
      console.error('Error checking user profile:', error);
      // On error, assume first time user
      setIsFirstTimeUser(true);
    }
  };

  const loadCalendars = async () => {
    try {
      const response = await axios.get(`${API}/calendars`, { withCredentials: true });
      const data = response?.data;
      
      // Safely handle response
      if (!data || !Array.isArray(data)) {
        console.warn('Calendars response is not an array:', data);
        setCalendars([]);
        setLoading(false);
        return;
      }
      
      setCalendars(data);
      
      if (data.length > 0 && !selectedCalendar) {
        setSelectedCalendar(data[0]);
      }
    } catch (error) {
      console.error('Error loading calendars:', error);
      // Don't show toast for network errors during initial load
      if (error.response?.status !== 401) {
        toast.error('Erreur lors du chargement des calendriers');
      }
      setCalendars([]);
    } finally {
      setLoading(false);
    }
  };

  // Handler for the unified Financial Setup Wizard
  const handleFinancialSetupComplete = async (calendarId, firstName) => {
    setShowFinancialSetup(false);
    setIsFirstTimeUser(false);
    
    // Update user's first name if provided (from profile step)
    if (firstName) {
      setUserFirstName(firstName);
    }
    
    // Reload calendars to get the newly created one
    await loadCalendars();
    toast.success(firstName ? `Bienvenue ${firstName} !` : 'Bienvenue dans ton espace budget !');
  };

  const handleLogout = async () => {
    if (onLogout) {
      // Use the prop if provided
      await onLogout();
    } else {
      // Fallback for backward compatibility
      try {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        setUser(null);
        navigate('/');
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
  };

  const handleCalendarCreated = (newCalendar) => {
    setCalendars([...calendars, newCalendar]);
    setSelectedCalendar(newCalendar);
    setShowCreateCalendar(false);
    toast.success('Calendrier créé !');
  };

  if (loading) {
    return (
      <TropicalBackground className="min-h-screen flex items-center justify-center">
        <motion.div 
          className="flex flex-col items-center gap-4"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <motion.div 
            className="w-14 h-14 border-3 border-primary border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
          <p className="text-muted-foreground font-medium">Chargement...</p>
        </motion.div>
      </TropicalBackground>
    );
  }

  if (calendars.length === 0) {
    return (
      <TropicalBackground className="min-h-screen safe-top safe-bottom">
        <div className="max-w-md mx-auto px-5 py-6">
          {/* Header - Premium Minimal */}
          <motion.div 
            className="flex justify-between items-center mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div>
              <h1 className="text-xl font-semibold text-foreground tracking-tight">
                {userFirstName ? `Salut ${userFirstName} ! 👋` : 'Bienvenue'}
              </h1>
              <p className="text-sm text-muted-foreground">Créons ton premier budget ensemble</p>
            </div>
            <Button 
              onClick={handleLogout} 
              variant="ghost" 
              size="icon"
              className="h-10 w-10 rounded-xl"
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5 text-muted-foreground" />
            </Button>
          </motion.div>

          {/* Configure financial foundation - Primary Entry Point */}
          <motion.div 
            className="premium-card text-center py-10"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
            >
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl shadow-lg flex items-center justify-center">
                <Building2 className="h-10 w-10 text-white" />
              </div>
            </motion.div>
            <h2 className="text-xl font-semibold text-foreground mb-2 tracking-tight">
              {isFirstTimeUser ? "C'est parti ! 🚀" : "Configurons ta base financière"}
            </h2>
            <p className="text-sm text-muted-foreground mb-8 max-w-xs mx-auto">
              {isFirstTimeUser 
                ? "Quelques étapes pour personnaliser ton espace et prendre le contrôle de ton budget"
                : "Définis tes revenus et dépenses pour que ton calendrier soit immédiatement opérationnel"
              }
            </p>
            <Button 
              onClick={() => setShowFinancialSetup(true)} 
              className="premium-btn-primary h-12 px-6 gap-2 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700"
              data-testid="configure-financial-base-btn"
            >
              <Building2 className="h-5 w-5" />
              {isFirstTimeUser ? "C'est parti !" : "Configurer ma base financière"}
            </Button>
          </motion.div>
        </div>

        {/* Unified Financial Setup Wizard (includes profile for first-time users) */}
        <FinancialSetupWizard
          open={showFinancialSetup}
          onComplete={handleFinancialSetupComplete}
          userName={user?.name}
          isFirstTimeUser={isFirstTimeUser}
        />

        {/* Keep CreateCalendarDialog for later use (adding more calendars) */}
        <CreateCalendarDialog
          open={showCreateCalendar}
          onClose={() => setShowCreateCalendar(false)}
          onCalendarCreated={handleCalendarCreated}
        />
      </TropicalBackground>
    );
  }

  return (
    <TropicalBackground className="min-h-screen safe-top">
      <div className="max-w-md mx-auto px-5 py-4 pb-32">
        {/* Header - Premium Compact */}
        <motion.div 
          className="flex items-center justify-between mb-5"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <img 
              src={user.picture} 
              alt={user.name} 
              className="w-10 h-10 rounded-xl object-cover shadow-sm"
            />
            <div className="min-w-0">
              <h1 className="text-base font-semibold text-foreground truncate tracking-tight">
                {userFirstName ? `Content de te revoir, ${userFirstName} 👋` : `Salut ${user.name.split(' ')[0]}`}
              </h1>
              <p className="text-xs text-muted-foreground truncate">Prêt à gérer ton budget ?</p>
            </div>
          </div>
          <div className="flex gap-1">
            <Button 
              onClick={() => setShowCreateCalendar(true)} 
              variant="ghost" 
              size="icon"
              className="h-9 w-9 rounded-xl"
              data-testid="new-calendar-btn"
            >
              <Plus className="h-5 w-5 text-muted-foreground" />
            </Button>
            <Button 
              onClick={handleLogout} 
              variant="ghost" 
              size="icon"
              className="h-9 w-9 rounded-xl"
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5 text-muted-foreground" />
            </Button>
          </div>
        </motion.div>

        {/* Calendar Selector */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <CalendarSelector
            calendars={calendars}
            selectedCalendar={selectedCalendar}
            onSelectCalendar={setSelectedCalendar}
            onCalendarsChanged={loadCalendars}
          />
        </motion.div>

        {/* Tabs - Réunion Premium Style */}
        {selectedCalendar && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-5">
              <TabsList className="tabs-container grid w-full grid-cols-5 h-14 p-1.5">
                <TabsTrigger 
                  value="calendar" 
                  className="rounded-xl data-[state=active]:tab-active flex flex-col gap-0.5 py-2 touch-manipulation data-[state=active]:text-cyan-600 text-slate-400"
                  data-testid="tab-calendar"
                >
                  <CalendarIcon className="h-5 w-5" />
                  <span className="text-[9px] font-semibold">Calendrier</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="foundation" 
                  className="rounded-xl data-[state=active]:tab-active flex flex-col gap-0.5 py-2 touch-manipulation data-[state=active]:text-cyan-600 text-slate-400"
                  data-testid="tab-foundation"
                >
                  <Building2 className="h-5 w-5" />
                  <span className="text-[9px] font-semibold">Base</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="savings" 
                  className="rounded-xl data-[state=active]:tab-active flex flex-col gap-0.5 py-2 touch-manipulation data-[state=active]:text-cyan-600 text-slate-400"
                  data-testid="tab-savings"
                >
                  <Target className="h-5 w-5" />
                  <span className="text-[9px] font-semibold">Objectifs</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="portefeuilles" 
                  className="rounded-xl data-[state=active]:tab-active flex flex-col gap-0.5 py-2 touch-manipulation data-[state=active]:text-cyan-600 text-slate-400"
                  data-testid="tab-portefeuilles"
                >
                  <Wallet className="h-5 w-5" />
                  <span className="text-[9px] font-semibold">Portefeuilles</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="astuces" 
                  className="rounded-xl data-[state=active]:tab-active flex flex-col gap-0.5 py-2 touch-manipulation data-[state=active]:text-cyan-600 text-slate-400"
                  data-testid="tab-astuces"
                >
                  <Lightbulb className="h-5 w-5" />
                  <span className="text-[9px] font-semibold">Astuces</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="calendar" className="mt-5">
                <BudgetCalendar 
                  key={calendarRefreshKey}
                  calendar={selectedCalendar} 
                  user={user} 
                  onLogout={handleLogout}
                  onViewFullHistory={() => setActiveTab('history')}
                />
              </TabsContent>

              <TabsContent value="foundation" className="mt-5">
                <FinancialFoundation 
                  calendar={selectedCalendar} 
                  onDataChanged={triggerCalendarRefresh}
                />
              </TabsContent>

              <TabsContent value="savings" className="mt-5">
                <SavingsGoals 
                  calendar={selectedCalendar} 
                  onDataChanged={triggerCalendarRefresh}
                />
              </TabsContent>

              <TabsContent value="portefeuilles" className="mt-5">
                <Portefeuilles 
                  calendar={selectedCalendar} 
                  onDataChanged={triggerCalendarRefresh}
                />
              </TabsContent>

              {/* Historique complet - accessible via bouton "Voir tout l'historique" */}
              <TabsContent value="history" className="mt-5">
                <div className="mb-4">
                  <Button
                    variant="ghost"
                    onClick={() => setActiveTab('calendar')}
                    className="text-sm text-muted-foreground hover:text-foreground gap-2"
                  >
                    ← Retour au calendrier
                  </Button>
                </div>
                <TransactionHistory calendar={selectedCalendar} />
              </TabsContent>

              <TabsContent value="astuces" className="mt-5">
                <AstucesTab />
              </TabsContent>
            </Tabs>
          </motion.div>
        )}

        <CreateCalendarDialog
          open={showCreateCalendar}
          onClose={() => setShowCreateCalendar(false)}
          onCalendarCreated={handleCalendarCreated}
        />

        <BirthdayCelebration />
      </div>
    </TropicalBackground>
  );
};

export default Dashboard;